/**
 * Das Ergebnis am Partieende (`R13`, Entscheidung des Users 2026-10-13): **eine Karte über dem
 * Brett**, oben das Urteil für den Betrachter, darunter Sieger, Grund und Stand. „Brett ansehen"
 * schließt sie, danach bleibt eine schmale Zeile unter der Kopfleiste (`ResultLine`).
 *
 * Gerechnet wird nichts hier — das Urteil kommt aus `resultOf`, der Sieger aus dem `outcome`
 * der Engine.
 */

import { useTranslation } from 'react-i18next';

import type { ResultView } from './result.js';

export interface ResultCardProps {
  readonly result: ResultView;
  /** Ein Spieler heißt beim Namen, eine Seite ist ein Team (dieselbe Regel wie im Statusbereich). */
  readonly label: (side: string) => string;
  readonly onDismiss: () => void;
}

/** Der Satz unter dem Urteil — dieselben Formulierungen wie im Statusbereich (`play:outcome`). */
function useDetail(result: ResultView, label: (side: string) => string): string {
  const { t } = useTranslation(['play']);
  if (result.verdict === 'aborted') return t('play:result.abortedBy');
  const reason = result.reason === undefined ? '' : t(`play:reason.${result.reason}`);
  if (result.winner === null || result.winner === undefined) return t('play:draw', { reason });
  return t('play:outcome', { winner: label(result.winner), reason });
}

function scoreLine(result: ResultView): string {
  return result.scores.map((entry) => entry.points).join(' : ');
}

export function ResultCard({ result, label, onDismiss }: ResultCardProps) {
  const { t } = useTranslation(['play']);
  const detail = useDetail(result, label);
  return (
    <div data-result-card={result.verdict} role="dialog" aria-label={t(`play:result.${result.verdict}`)}>
      <p className="verdict">{t(`play:result.${result.verdict}`)}</p>
      <p>{detail}</p>
      {result.scores.length < 2 ? null : (
        <p className="score">{t('play:result.score', { score: scoreLine(result) })}</p>
      )}
      <button type="button" onClick={onDismiss}>
        {t('play:result.dismiss')}
      </button>
    </div>
  );
}

export function ResultLine({ result, label }: Omit<ResultCardProps, 'onDismiss'>) {
  const { t } = useTranslation(['play']);
  const detail = useDetail(result, label);
  return (
    <p data-result-line={result.verdict} role="status">
      <strong>{t(`play:result.${result.verdict}`)}</strong> — {detail}
    </p>
  );
}
